/**
 * RuleDetailsPanel Component
 *
 * Side panel (Sheet) displaying detailed information about a learned rule:
 * - Complete rule information
 * - Performance metrics and trends
 * - Application history
 * - Related corrections
 * - Quick actions
 */

import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LearningRulesService } from '@/services/learningRulesService';
import { useToast } from '@/hooks/use-toast';
import type { LearnedRule, RuleApplicationHistory } from '@/types/learningRules';
import {
  Loader2,
  TrendingUp,
  TrendingDown,
  Calendar,
  CheckCircle2,
  XCircle,
  Edit,
  Copy,
  Share2,
  Power,
  AlertTriangle,
  History,
} from 'lucide-react';

interface RuleDetailsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: LearnedRule | null;
  mandateName?: string;
  onEdit?: (rule: LearnedRule) => void;
  onCopy?: (rule: LearnedRule) => void;
  onShare?: (rule: LearnedRule) => void;
  onToggleStatus?: (rule: LearnedRule) => void;
}

export const RuleDetailsPanel: React.FC<RuleDetailsPanelProps> = ({
  open,
  onOpenChange,
  rule,
  mandateName,
  onEdit,
  onCopy,
  onShare,
  onToggleStatus,
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<RuleApplicationHistory[]>([]);

  useEffect(() => {
    if (rule && open) {
      loadHistory();
    }
  }, [rule, open]);

  const loadHistory = async () => {
    if (!rule) return;

    // System rules (hardcoded) don't have history in the database
    if (rule.id.startsWith('hardcoded-')) {
      setHistory([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const applicationHistory = await LearningRulesService.getRuleApplicationHistory(rule.id);
      setHistory(applicationHistory);
    } catch (error) {
      console.error('Error loading rule history:', error);
      toast({
        title: 'Fehler',
        description: 'Historie konnte nicht geladen werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!rule) return null;

  // Calculate metrics
  const successRate = rule.usage_count > 0
    ? (rule.success_count / rule.usage_count) * 100
    : 0;

  const confidenceStatus = rule.current_confidence >= 0.9
    ? { label: 'Excellent', color: 'text-green-600', emoji: '🟢' }
    : rule.current_confidence >= 0.75
    ? { label: 'Good', color: 'text-yellow-600', emoji: '🟡' }
    : { label: 'Needs Review', color: 'text-red-600', emoji: '🔴' };

  const confidenceTrend =
    rule.current_confidence > rule.base_confidence ? 'up' : 'down';

  const getPatternTypeLabel = () => {
    switch (rule.pattern_type) {
      case 'counterparty': return 'Counterparty';
      case 'description_pattern': return 'Description';
      case 'amount_based': return 'Amount-Based';
      case 'semantic': return 'Semantic';
      default: return rule.pattern_type;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl w-full overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span>Regel Details</span>
            {rule.is_active ? (
              <Badge variant="default">Aktiv</Badge>
            ) : (
              <Badge variant="secondary">Inaktiv</Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            Detaillierte Informationen und Anwendungshistorie
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1 -mr-6 pr-6">
          <div className="space-y-6 py-4">
            {/* Mandate Info */}
            {mandateName && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">Mandat</div>
                <Badge className="text-base px-3 py-1">{mandateName}</Badge>
              </div>
            )}

            {/* Pattern Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Pattern</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="text-sm text-muted-foreground">Type</div>
                  <Badge variant="outline" className="mt-1">
                    {getPatternTypeLabel()}
                  </Badge>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground">Key</div>
                  <div className="text-lg font-semibold mt-1">
                    {rule.pattern_key}
                  </div>
                </div>

                {rule.pattern_value && (
                  <div>
                    <div className="text-sm text-muted-foreground">Value</div>
                    <div className="text-sm mt-1">{rule.pattern_value}</div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Performance Metrics */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Performance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Confidence */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Confidence
                    </div>
                    <div className="text-3xl font-bold mt-1">
                      {(rule.current_confidence * 100).toFixed(0)}%
                    </div>
                    <div className={`text-sm mt-1 flex items-center gap-1 ${confidenceStatus.color}`}>
                      <span>{confidenceStatus.emoji}</span>
                      <span>{confidenceStatus.label}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Trend</div>
                    {confidenceTrend === 'up' ? (
                      <TrendingUp className="h-8 w-8 text-green-600 mt-1" />
                    ) : (
                      <TrendingDown className="h-8 w-8 text-red-600 mt-1" />
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      Base: {(rule.base_confidence * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Success Rate */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Success Rate
                    </div>
                    <div className="text-2xl font-bold mt-1">
                      {successRate.toFixed(0)}%
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {rule.success_count} / {rule.usage_count}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-muted-foreground">Usage</div>
                    <div className="text-2xl font-bold mt-1">
                      {rule.usage_count}x
                    </div>
                    {rule.correction_count > 0 && (
                      <div className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        {rule.correction_count} corrected
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Mapping */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Kontenzuordnung</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Debit</div>
                    <div className="text-base font-semibold mt-1">
                      {rule.suggested_debit_account}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Credit</div>
                    <div className="text-base font-semibold mt-1">
                      {rule.suggested_credit_account}
                    </div>
                  </div>
                </div>

                {(rule.vat_code || rule.vat_account) && (
                  <>
                    <Separator />
                    <div>
                      <div className="text-sm text-muted-foreground mb-2">
                        MWST
                      </div>
                      <div className="space-y-1">
                        {rule.vat_code && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Code:</span>
                            <span className="ml-2 font-medium">
                              {rule.vat_code}
                            </span>
                          </div>
                        )}
                        {rule.vat_account && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Konto:</span>
                            <span className="ml-2 font-medium">
                              {rule.vat_account}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Metadata */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Metadata</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {rule.learned_from_timestamp && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Gelernt am</span>
                    <span className="font-medium flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(rule.learned_from_timestamp).toLocaleDateString('de-CH')}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Erstellt am</span>
                  <span className="font-medium">
                    {new Date(rule.created_at).toLocaleDateString('de-CH')}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Aktualisiert am</span>
                  <span className="font-medium">
                    {new Date(rule.updated_at).toLocaleDateString('de-CH')}
                  </span>
                </div>

                {rule.is_shared && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Scope</span>
                    <Badge variant="secondary">
                      <Share2 className="h-3 w-3 mr-1" />
                      {rule.scope}
                    </Badge>
                  </div>
                )}

                {rule.is_deprecated && (
                  <div className="pt-2 border-t">
                    <div className="text-sm text-destructive flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" />
                      <strong>Deprecated</strong>
                    </div>
                    {rule.deprecated_reason && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {rule.deprecated_reason}
                      </p>
                    )}
                    {rule.deprecated_at && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(rule.deprecated_at).toLocaleDateString('de-CH')}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Application History */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Anwendungshistorie
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : history.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    Noch keine Anwendungen
                  </p>
                ) : (
                  <div className="space-y-3">
                    {history.slice(0, 10).map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start gap-3 text-sm border-l-2 pl-3 py-1"
                        style={{
                          borderColor: item.was_accepted
                            ? 'rgb(34, 197, 94)'
                            : 'rgb(239, 68, 68)',
                        }}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {item.was_accepted ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {item.counterparty || item.description || 'Transaction'}
                          </div>
                          {item.amount && (
                            <div className="text-xs text-muted-foreground">
                              CHF {item.amount.toFixed(2)}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground">
                            {new Date(item.created_at).toLocaleString('de-CH')}
                          </div>
                          {item.confidence_at_application && (
                            <div className="text-xs text-muted-foreground">
                              Confidence: {(item.confidence_at_application * 100).toFixed(0)}%
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {history.length > 10 && (
                      <p className="text-xs text-muted-foreground text-center pt-2">
                        +{history.length - 10} weitere Anwendungen
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Aktionen</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {onEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onEdit(rule);
                      onOpenChange(false);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}

                {onToggleStatus && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onToggleStatus(rule);
                      onOpenChange(false);
                    }}
                  >
                    <Power className="h-4 w-4 mr-2" />
                    {rule.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  </Button>
                )}

                {onShare && !rule.is_shared && rule.is_active && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onShare(rule);
                      onOpenChange(false);
                    }}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Teilen
                  </Button>
                )}

                {onCopy && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onCopy(rule);
                      onOpenChange(false);
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Kopieren
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default RuleDetailsPanel;
